import { useState, useCallback } from 'react';
import { ExternalLink, UserX, Contact, Package, Video, Droplets, Mail, ArrowRight } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { NavBar } from '../components/NavBar';
import { Footer } from '../components/Footer';
import { PageHead } from '../components/PageHead';
import { StructuredData, breadcrumb } from '../components/StructuredData';
import {
  DOCTOLIB_RUSSEY_ADAPTATION_LENTILLES,
  DOCTOLIB_RUSSEY_CONSULTATION_VIDEO,
  DOCTOLIB_OPHTALMO_BESANCON,
} from '../doctolibLinks';
import { track } from '../utils/umami';
import '../App.css';

const LENTILLES_EMAIL = 'lunetteriedurussey@gmail.com';

export function LentillesPage() {
  const [step, setStep]               = useState('menu');
  const [mailName, setMailName]       = useState('');
  const [mailError, setMailError]     = useState('');

  const reset = useCallback(() => {
    setStep('menu');
    setMailName('');
    setMailError('');
  }, []);

  const openRenewalMail = useCallback(() => {
    setMailError('');
    const name = mailName.trim();
    if (!name) {
      setMailError("Indiquez votre nom et prénom pour l’objet du courriel, s’il vous plaît.");
      return;
    }
    const subject = `Renouvellement de lentille — ${name}`;
    const body =
      'Bonjour,\n\n' +
      `Je souhaite renouveler la même commande de lentilles qu'en magasin.\n` +
      `Client(e) : ${name}\n\nBonne journée.`;
    track('email-renouvellement-lentilles');
    window.open(
      `mailto:${LENTILLES_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }, [mailName]);

  return (
    <div className="App">
      <PageHead
        title="Lentilles de contact – Lunetterie du Russey | Le Russey (25210)"
        description="Première paire, renouvellement ou nouvelle ordonnance : trouvez le bon parcours pour vos lentilles de contact. Adaptation en magasin au Russey, Haut-Doubs."
      />
      <StructuredData data={breadcrumb([
        { name: 'Accueil', path: '/' },
        { name: 'Lentilles de contact', path: '/lentilles' },
      ])} />
      <NavBar />

      <section
        id="lentilles"
        className="lunettes-section lentilles-section page-section-first wave-bottom-to-dark"
        aria-labelledby="lentilles-heading"
      >
        <div className="section-container">
          <div className="section-header">
            <Badge className="section-badge">Lentilles de contact</Badge>
            <h1 className="section-title" id="lentilles-heading">
              Lentilles de contact : par quelle porte entrer ?
            </h1>
            <p className="section-description lunettes-section-lead">
              Première paire, ordonnance en poche ou renouvellement : voici l&apos;étape logique
              et le bon interlocuteur, sans prise de tête.
            </p>
          </div>

          {step === 'menu' && (
            <div className="lunettes-choice-grid" role="group" aria-label="Votre situation vis-à-vis des lentilles">
              <button type="button" className="lunettes-choice-card" onClick={() => { track('parcours-lentilles', { etape: 'sans-ordonnance' }); setStep('sans'); }}>
                <div className="lunettes-choice-icon" aria-hidden><UserX className="lunettes-choice-icon-svg" /></div>
                <h2 className="lunettes-choice-title">Je n&apos;en porte pas, je n&apos;ai pas d&apos;ordonnance</h2>
                <p className="lunettes-choice-text">
                  Vous n&apos;avez jamais porté de lentilles : avant tout essai, il faut s&apos;assurer que
                  c&apos;est possible pour vous — et cela commence chez l&apos;ophtalmologiste, en cabinet.
                </p>
                <span className="lunettes-choice-cta">Comprendre l&apos;étape <ArrowRight className="h-4 w-4" aria-hidden /></span>
              </button>

              <button type="button" className="lunettes-choice-card lunettes-choice-card--accent" onClick={() => { track('parcours-lentilles', { etape: 'porteur' }); setStep('porteur'); }}>
                <div className="lunettes-choice-icon" aria-hidden><Contact className="lunettes-choice-icon-svg" /></div>
                <h2 className="lunettes-choice-title">J&apos;en ai déjà porté ou j&apos;ai une ordonnance pour un essai</h2>
                <p className="lunettes-choice-text">
                  Vous savez à peu près ce dont vous avez besoin : renouveler une commande (même type),
                  faire renouveler l&apos;ordonnance, ou lancer les essais avec une ordonnance récente.
                </p>
                <span className="lunettes-choice-cta">Voir les options <ArrowRight className="h-4 w-4" aria-hidden /></span>
              </button>
            </div>
          )}

          {step === 'sans' && (
            <Card className="lunettes-result-card max-w-2xl mx-auto shadow-sm">
              <CardHeader>
                <CardTitle className="text-2xl font-['Roboto_Slab',serif]">
                  Première paire de lentilles : côté ophtalmo, en cabinet
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-base text-[#555] space-y-4 leading-relaxed max-w-prose">
                  <p>
                    Pour lancer un vrai <strong>essai de lentilles de contact</strong>, il faut d&apos;abord une{' '}
                    <strong>consultation auprès d&apos;un ophtalmologiste, en présentiel</strong>. C&apos;est l&apos;étape
                    qui permet de vérifier l&apos;aptitude, de mesurer les courbures, et de prescrire si tout est en
                    ordre — <strong>avant toute</strong> commande chez l&apos;opticien.
                  </p>
                  <p>
                    <strong>La téléconsultation ne remplace pas cette étape</strong> : une{' '}
                    <strong>adaptation de lentilles</strong> n&apos;est pas réalisable par visioconférence. Si vous
                    n&apos;avez jamais porté de lentilles, orientez d&apos;abord votre prise de rendez-vous vers{' '}
                    <strong>un ophtalmologue (Besançon, ci-dessous)</strong>.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3 pt-2">
                  <Button asChild className="w-full sm:w-auto bg-[var(--color-anthracite)] hover:opacity-95 text-white">
                    <a href={DOCTOLIB_OPHTALMO_BESANCON} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2" onClick={() => track('clic-doctolib', { type: 'ophtalmo-besancon', source: 'lentilles' })}>
                      <ExternalLink className="h-4 w-4" />
                      Trouver un ophtalmo (Besançon) sur Doctolib
                    </a>
                  </Button>
                  <Button type="button" variant="link" className="w-full sm:w-auto text-[var(--color-anthracite)]" onClick={reset}>
                    Revenir au choix précédent
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 'porteur' && (
            <div className="lentilles-subgrid" role="group" aria-label="Renouvellement, ordonnance, essai">
              <Card className="lentilles-subcard h-full flex flex-col">
                <CardHeader>
                  <div className="lentilles-sub-icon" aria-hidden><Package className="h-7 w-7" /></div>
                  <CardTitle>J&apos;ai déjà passé une commande ici, je la renouvelle</CardTitle>
                  <div className="text-sm text-muted-foreground text-left">
                    Même type de lentilles, mêmes paramètres : l&apos;équipe règle le suivi. Envoyez un message pour lancer le renouvellement.
                  </div>
                </CardHeader>
                <CardContent className="mt-auto space-y-3 text-left">
                  <div>
                    <Label htmlFor="lentille-mail-name" className="text-base">Nom et prénom</Label>
                    <Input id="lentille-mail-name" type="text" autoComplete="name" className="mt-1.5"
                      value={mailName} onChange={(e) => { setMailName(e.target.value); if (mailError) setMailError(''); }}
                      placeholder="Ex. Marie Martin" />
                    {mailError && <p className="text-sm text-destructive mt-1" role="alert">{mailError}</p>}
                  </div>
                  <Button type="button" className="w-full sm:w-auto bg-forest hover:bg-forest-light text-white gap-2" onClick={openRenewalMail}>
                    <Mail className="h-4 w-4" />
                    Nous écrire par courriel
                  </Button>
                </CardContent>
              </Card>

              <Card className="lentilles-subcard h-full flex flex-col">
                <CardHeader>
                  <div className="lentilles-sub-icon" aria-hidden><Video className="h-7 w-7" /></div>
                  <CardTitle>Ordonnance de plus de trois ans</CardTitle>
                  <div className="text-sm text-muted-foreground text-left">
                    L&apos;ordonnance n&apos;est plus valable pour l&apos;équipement : il faut la <strong>renouveler</strong> avant de commander. Selon le cas, un parcours en <strong>consultation vidéo</strong> avec la lunetterie peut convenir.
                  </div>
                </CardHeader>
                <CardContent className="mt-auto">
                  <p className="text-sm text-[#555] mb-3 text-left">Sur notre fiche, choisissez la consultation <strong>vidéo</strong> :</p>
                  <Button asChild className="w-full bg-rust hover:bg-rust-light text-white gap-2">
                    <a href={DOCTOLIB_RUSSEY_CONSULTATION_VIDEO} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2" onClick={() => track('clic-doctolib', { type: 'consultation-video-russey' })}>
                      <ExternalLink className="h-4 w-4" />
                      Consultation vidéo (Doctolib) — Le Russey
                    </a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="lentilles-subcard h-full flex flex-col">
                <CardHeader>
                  <div className="lentilles-sub-icon" aria-hidden><Droplets className="h-7 w-7" /></div>
                  <CardTitle>Ordonnance de lentilles, besoin d&apos;essais en magasin</CardTitle>
                  <div className="text-sm text-muted-foreground text-left">
                    Votre ordo est à jour : place aux <strong>essais</strong>, <strong>ajustement</strong> et <strong>formation à l&apos;entretien</strong> avec l&apos;opticien, sur place.
                  </div>
                </CardHeader>
                <CardContent className="mt-auto">
                  <p className="text-sm text-[#555] mb-3 text-left">Sur notre fiche, choisissez <strong>adaptation de lentilles</strong> :</p>
                  <Button asChild className="w-full bg-[var(--color-anthracite)] hover:opacity-95 text-white gap-2">
                    <a href={DOCTOLIB_RUSSEY_ADAPTATION_LENTILLES} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2" onClick={() => track('clic-doctolib', { type: 'adaptation-lentilles-russey' })}>
                      <ExternalLink className="h-4 w-4" />
                      Adaptation de lentilles (Doctolib) — Le Russey
                    </a>
                  </Button>
                </CardContent>
              </Card>

              <div className="lentilles-subgrid-back">
                <Button type="button" variant="link" className="h-auto p-0 text-[var(--color-anthracite)]" onClick={reset}>
                  Revenir au choix des lentilles
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
